import {
  AlertLevel,
  Plaid_CreatePlaidSessionMutation,
  Plaid_CreatePlaidSessionMutationVariables,
} from '@/types';
import { gql, useMutation } from '@apollo/client';
import { openPlaidInBrowser } from '@/services/plaid';
import { useCallback } from 'react';
import { useUserContext } from '@/contexts/user-context';

export const PLAID_CREATE_PLAID_SESSION_MUTATION = gql`
  mutation Plaid_CreatePlaidSession(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    plaidCreateSession(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      alert_level
      alert_message
      session_token
    }
  }
`;

/**
 * Hook for initiating Plaid Link.
 *
 * Returns an async function that will being the Plaid Link
 * process in a new browser window/tab. The function raises
 * Errors on unexpected GraphQL response, and resolves when
 * the browser has been closed.
 */
export const useInitiatePlaid = () => {
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();

  const [createPlaidSessionToken] = useMutation<
    Plaid_CreatePlaidSessionMutation,
    Plaid_CreatePlaidSessionMutationVariables
  >(PLAID_CREATE_PLAID_SESSION_MUTATION);

  const initiatePlaid = useCallback(async () => {
    const response = await createPlaidSessionToken({
      variables: {
        restaurantUUID,
        supplierUUID,
      },
    });

    if (
      response.data?.plaidCreateSession.alert_level === AlertLevel.Positive &&
      response.data?.plaidCreateSession.session_token
    ) {
      // Return the web browser Promise, which allows consumers of this
      // hook to know when the browser has been closed and react accordingly
      return openPlaidInBrowser(
        response.data?.plaidCreateSession.session_token,
      );
    } else {
      throw Error('Error creating Plaid session');
    }
  }, [createPlaidSessionToken, restaurantUUID, supplierUUID]);

  return initiatePlaid;
};
